import {useState} from 'react';

import {InfoOutlined} from '@mui/icons-material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import DeleteForever from '@mui/icons-material/DeleteForeverOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import {Divider, ListItemIcon, Menu, MenuItem, useTheme} from '@mui/material';
import Typography from '@mui/material/Typography';

import {SNACKBAR_ACTIONS} from '../../const';
import usePayment from '../../hooks/providers/usePayment';
import useProfile from '../../hooks/providers/useProfile';
import useSnackbar from '../../hooks/providers/useSnackbar';
import useWorkspaces from '../../hooks/providers/useWorkspaces';
import {downloadFileFromApiRawResult} from '../../utils';

// eslint-disable-next-line complexity
const ReportTabsMenu = () => {
  const {
    setCreateReportModalOpen,
    setDeleteWorkspaceOrReportModalOpen,
    selectedWorkspaceId,
    exportReport,
    setIsCustomReportCreation,
    anchorElReportTabsMenu: anchorEl,
    setAnchorElReportTabsMenu,
    rightClickedReportTab: report,
    setIsWorkspaceUsersModalOpen,
    isUserInWorkspace,
    selectedWorkspace
  } = useWorkspaces();
  const {profile} = useProfile();
  const {showSnackbar, closeSnackbar, defaultSnackbarOptions} = useSnackbar();
  const {isAdmin} = usePayment();

  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const theme = useTheme();
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorElReportTabsMenu(null);
  };

  const downloadReport = async () => {
    setIsReportDownloading(true);
    const snackbarId = showSnackbar(
      SNACKBAR_ACTIONS.EXPORT_REPORT_IN_PROGRESS,
      {
        severity: 'warning',
        autoHide: false,
        hasSpinner: true
      },
      {exportedReportName: report.report_name}
    );

    const res = await exportReport(selectedWorkspaceId, report.report_id);
    closeSnackbar(snackbarId);

    if (res.success) {
      downloadFileFromApiRawResult(res.data, `${report.report_name}.pbix`);
      showSnackbar(SNACKBAR_ACTIONS.EXPORT_REPORT_SUCCESS, defaultSnackbarOptions, {exportedReportName: report.report_name});
    }

    setIsReportDownloading(false);
    handleClose();
  };

  if (!report) return null;

  const userIsInWorkspace = isUserInWorkspace(profile.id, selectedWorkspace);

  const isDownloadButtonDisabled = () => {
    if (isReportDownloading || !report.isCustomModel) return true;
    if (!isAdmin && !userIsInWorkspace) return true;
    return false;
  };

  return (
    <Menu
      id="workspaces-tabs-menu"
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={open}
      onClose={handleClose}
      slotProps={{paper: {sx: {width: 300}}}}
    >
      {report.size_megaoctets && (userIsInWorkspace || isAdmin) ? (
        <>
          <MenuItem disabled sx={{py: 1.5}}>
            <ListItemIcon>
              <InfoOutlined color="primary" />
            </ListItemIcon>
            <Typography noWrap fontSize={14} variant="inherit">
              Taille du modèle : {report.size_megaoctets} Mo
            </Typography>
          </MenuItem>
          <Divider sx={{margin: '0 !important'}} />
        </>
      ) : null}

      <MenuItem
        disabled={!isAdmin && !userIsInWorkspace}
        key={2}
        sx={{pt: 1.5}}
        onClick={() => {
          setIsWorkspaceUsersModalOpen(true);
          handleClose();
        }}
      >
        <ListItemIcon>
          <GroupRoundedIcon color="primary" />
        </ListItemIcon>
        <Typography fontSize={14} variant="inherit">
          Gérer les utilisateurs
        </Typography>
      </MenuItem>
      <Divider sx={{margin: '0 !important'}} />
      {report.isCustomModel && (
        <MenuItem
          disabled
          sx={{py: 1.5}}
          onClick={() => {
            setCreateReportModalOpen(true);
            setIsCustomReportCreation(true);
            handleClose();
          }}
        >
          <ListItemIcon>
            <CloudSyncIcon fontSize="medium" color="primary" />
          </ListItemIcon>
          <Typography fontSize={14} variant="inherit">
            Synchroniser le rapport
          </Typography>
        </MenuItem>
      )}

      <MenuItem disabled={isDownloadButtonDisabled()} sx={{py: 1.5}} onClick={downloadReport}>
        <ListItemIcon>
          <DownloadIcon fontSize="medium" color="primary" />
        </ListItemIcon>
        <Typography noWrap fontSize={14} variant="inherit">
          {isReportDownloading ? 'Téléchargement du rapport en cours ...' : `Télécharger ${report.report_name}`}
        </Typography>
      </MenuItem>
      <Divider sx={{margin: '0 !important'}} />

      <MenuItem
        disabled={!isAdmin && !userIsInWorkspace}
        sx={{pt: 1.5}}
        onClick={() => {
          setDeleteWorkspaceOrReportModalOpen(true);
          handleClose();
        }}
      >
        <ListItemIcon>
          <DeleteForever fontSize="medium" color="error" />
        </ListItemIcon>
        <Typography noWrap fontSize={14} sx={{color: theme.palette.error.main}} variant="inherit">
          Supprimer {report.report_name}
        </Typography>
      </MenuItem>
    </Menu>
  );
};

export default ReportTabsMenu;
